import * as mutationTypes from '../mutation-types'
import * as actionTypes from '../action-types'
import server from '../server'
import store from '../../store'
import jwtDecode from 'jwt-decode'
import axios from 'axios'

const state = {
  loggedIn: false,
  loginToken: '',
  loginError: false,
  sessionExpired: false,
  loggedOutSnackbar: false,
  sessionExpires: null,
  roles: [],
  featureFlags: [],
  username: '',
  studentId: '',
  title: '',
  firstName: '',
  lastName: '',
  darkTheme: false,
  sessionExpiredActions: [],
  isTutor: false,
  chartLimitReached: false,
  previousRoute: {},
  module: {
    alert: 'ALERT',
    attendance: 'ATTENDANCE',
    assessment: 'ASSESSMENT',
    engagement: 'ENGAGEMENT',
    interexem: 'INTEREXEM',
    insights: 'INSIGHTS',
    feeds: 'FEEDS',
    student: 'STUDENT',
    help: 'HELP'
  },
  functions: {
    adminMenu: 'ADMINMNU',
    headerMenu: 'HEADERMNU',
    helpPopUp: 'HELPPOPUP'
  },
  options: {
    enable: {
      alerts: 'ENBALERTS',
      interactions: 'ENBINTEACT',
      helpPopUp: 'ENBHELPPOP'
    }
  }
}

const getters = {
  IS_ADMIN (state) {
    return state.roles.indexOf('ROLE_ADMIN') >= 0
  },
  IS_STUDENT (state) {
    return state.roles.indexOf('ROLE_STUDENT') >= 0
  },
  IS_STAFF (state) {
    return state.roles.indexOf('ROLE_STAFF') >= 0
  },
  USER_NAME (state) {
    return (state.title ? state.title + ' ' : '') +
        (state.firstName ? state.firstName + ' ' : '') +
        (state.lastName ? state.lastName : '')
  },
  HAS_FEATURE_FLAG: (state) => (flag) => {
    return state.featureFlags.indexOf(flag) >= 0
  },
  IS_TUTOR (state) {
    return state.isTutor
  }
}

function updateLoginToken (state, loginToken) {
  state.loggedIn = true
  state.sessionExpired = false
  state.loginToken = loginToken
  state.loginError = false
  const decoded = jwtDecode(loginToken.substring(7))
  state.roles = decoded.roles.split(',')
  state.username = decoded.sub.toLowerCase().trim()
  state.featureFlags = decoded['product-feature-flags']
  if (state.featureFlags === undefined) {
    state.featureFlags = []
  }
  if (decoded.details) {
    state.title = decoded.details.title
    state.firstName = decoded.details.firstName
    state.lastName = decoded.details.lastName
    state.studentId = decoded.details.studentId
  }
  state.sessionExpires = decoded.exp * 1000
}

const mutations = {
  [mutationTypes.LOGGED_IN] (state, loginToken) {
    updateLoginToken(state, loginToken)
  },
  [mutationTypes.REFRESH_AUTHORIZATION] (state, loginToken) {
    updateLoginToken(state, loginToken)
  },
  [mutationTypes.LOGGED_OUT] (state) {
    state.loggedIn = false
    state.loginToken = ''
    server.removeAuthOnPage()
    state.loggedOutSnackbar = true
    state.username = ''
    state.studentId = ''
    state.title = ''
    state.firstName = ''
    state.lastName = ''
    state.sessionExpired = false
    state.sessionExpiredActions = []
    state.isTutor = false
    if (store.state.config.useSAML) {
      window.location.replace(store.state.config.authUrlLogOut)
    }
  },
  [mutationTypes.SET_PREVIOUS_ROUTE] (state, name) {
    state.previousRoute = name
  },
  [mutationTypes.LOG_IN_FAILURE] (state) {
    state.loginError = true
  },
  [mutationTypes.LOGOUT_SNACKBAR_OFF] (state) {
    state.loggedOutSnackbar = false
  },
  [mutationTypes.SET_PROFILE_DARK_THEME] (state, value) {
    state.darkTheme = value
  },
  [mutationTypes.SAVE_SESSION_EXPIRED_ACTION] (state, actionData) {
    state.sessionExpired = true
    state.sessionExpiredActions.push(actionData)
  },
  [mutationTypes.CLEAR_SESSION_EXPIRED_ACTIONS] (state) {
    state.sessionExpiredActions = []
  },
  [mutationTypes.SET_IS_TUTOR] (state, isNotTutor) {
    state.isTutor = !isNotTutor
  },
  [mutationTypes.SET_CHART_LIMIT_REACHED] (state, limitReached) {
    state.chartLimitReached = limitReached
  }
}

const actions = {
  [actionTypes.LOGIN] (context, credentials) {
    let {username, password} = credentials
    server.unauthPost('/core/login', {username, password}, response => {
      context.commit(mutationTypes.LOGGED_IN, response.headers.authorization)
      server.setAuthOnPage(response.headers.authorization)
      context.dispatch(actionTypes.APPLY_SESSION_EXPIRED_ACTIONS)
      context.dispatch(actionTypes.GET_TUTOR_BOOLEAN)
      context.dispatch(actionTypes.GET_CHART_LIMIT_BOOLEAN)
    }, () => {
      context.commit(mutationTypes.LOG_IN_FAILURE)
    })
  },
  [actionTypes.LOGOUT] (context) {
    axios({
      url: `${context.rootState.config.baseUrl}:${context.rootState.config.port}/core/token/destroy`,
      method: 'post',
      headers: {
        'Authorization': context.rootState.profile.loginToken
      }
    })
    context.commit(mutationTypes.LOGGED_OUT)
  },
  [actionTypes.APPLY_SESSION_EXPIRED_ACTIONS] ({state, commit, dispatch}) {
    // Prevent grouped calls being repeated
    let processed = new Set()
    state.sessionExpiredActions.forEach(({action, data}) => {
      if (!processed.has(action)) {
        dispatch(action, data)
        processed.add(action)
      }
    })
    commit(mutationTypes.CLEAR_SESSION_EXPIRED_ACTIONS)
  },
  [actionTypes.REFRESH_LOGIN] ({state, commit}, {config, headers, request}) {
    if ((headers.authorization) && !config.url.endsWith('/login')) {
      if (headers.authorization !== state.loginToken) {
        commit(mutationTypes.REFRESH_AUTHORIZATION, headers.authorization)
        server.setAuthOnPage(headers.authorization)
      }
    }
  },
  [actionTypes.GET_TUTOR_BOOLEAN] ({commit}) {
    const actionData = { action: actionTypes.GET_TUTOR_BOOLEAN, data: undefined }
    server.getReplayable(`/core/staff/isTutor`, actionData).then(response => {
      // set boolean for 'SET_IS_TUTOR' (which is assigned to the state of dashboard, my studnets and interactions)
      commit(mutationTypes.SET_IS_TUTOR, response.data)
    })
  },
  [actionTypes.GET_CHART_LIMIT_BOOLEAN] ({commit}) {
    const actionData = { action: actionTypes.GET_CHART_LIMIT_BOOLEAN, data: undefined }
    server.getReplayable(`/core/chart/isLimitReached`, actionData).then(response => {
      commit(mutationTypes.SET_CHART_LIMIT_REACHED, response.data)
    }).catch((err) => {
      console.log('Failed to check chart limit')
      console.error(err)
    })
  },
  [actionTypes.LOGIN_SSO_ACTION] ({commit, dispatch}, token) {
    let username = token
    let password = ''
    // server.unauthPost(`/core/auth/ssologin/${token}`, {}, response => {
    server.unauthPost('/core/ssologin', {username, password}, response => {
      commit(mutationTypes.LOGGED_IN, response.headers.authorization)
      server.setAuthOnPage(response.headers.authorization)
      dispatch(actionTypes.APPLY_SESSION_EXPIRED_ACTIONS)
      dispatch(actionTypes.GET_TUTOR_BOOLEAN)
      dispatch(actionTypes.GET_CHART_LIMIT_BOOLEAN)
    }, () => {
      commit(mutationTypes.LOG_IN_FAILURE)
    })
  }
}

export default {
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/profileModule.js